import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowUpRight, MessageCircle, KeyRound } from 'lucide-react';
import { api } from '../../lib/api';
import { SiteLayout } from '../../components/site/SiteLayout';
import { PillLink, PillButton } from '../../components/site/Pill';
import { SchemeGrid } from '../../components/site/SchemeGrid';
import { openSiteChat } from '../../components/SiteChat';
import { FOOTER_MENU } from '../../lib/site';
import { formatRent, bedsLabel, type Listing } from './ListingCard';

/**
 * Contact. lotusliving.co.uk puts an enquiry form here; the brief asked for the
 * available homes instead, with the assistant as the way to talk to us.
 */
export default function SiteContact() {
  const { data: listings } = useQuery({ queryKey: ['public-listings', 'contact'], queryFn: async () => (await api.public.get<Listing[]>('/public/listings', { perPage: 8, sort: 'rent' })).data });

  return (
    <SiteLayout>
      <section className="site-narrow pt-20 lg:pt-32">
        <p className="eyebrow">Contact</p>
        <h1 className="display mt-4 max-w-[980px] text-[44px] text-ink-900 sm:text-[60px] lg:text-[80px]">We believe that everybody deserves a place that <em>feels like home</em></h1>
      </section>

      <section className="site-narrow grid gap-12 py-20 lg:grid-cols-5 lg:gap-20 lg:py-28">
        <div className="lg:col-span-3">
          <div className="flex items-end justify-between">
            <h2 className="display text-[32px] text-ink-900 sm:text-[40px]">Available <em>now</em></h2>
            <Link to="/rent" className="copy-link text-[14px] font-medium">View all homes</Link>
          </div>
          <ul className="mt-6 divide-y divide-ink-100 border-y border-ink-200">
            {(listings ?? []).map((l) => (
              <li key={l.id}>
                <Link to={`/rent/${l.slug}`} className="group flex items-center justify-between gap-4 py-4">
                  <div className="min-w-0">
                    <div className="text-[11px] font-medium uppercase tracking-[0.18em] text-ink-400">{l.property?.city}</div>
                    <div className="mt-0.5 truncate text-[16px] text-ink-900 group-hover:text-bronze-600">{l.property?.name} <span className="text-ink-400">·</span> {bedsLabel(l.unit?.bedrooms)}</div>
                  </div>
                  <div className="flex shrink-0 items-center gap-4">
                    <span className="text-[15px] font-medium text-ink-900">{formatRent(l.advertisedRentMinor, l.advertisedRentCurrency)} <span className="text-[12px] font-normal text-ink-400">{l.rentPeriod === 'YEAR' ? 'pa' : 'pcm'}</span></span>
                    <ArrowUpRight className="h-4 w-4 text-ink-300 transition group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-bronze-500" />
                  </div>
                </Link>
              </li>
            ))}
            {!listings && <li className="py-10 text-center text-sm text-ink-400">Loading availability…</li>}
          </ul>
        </div>

        <aside className="space-y-4 lg:col-span-2">
          <div className="rounded-[10px] bg-ink-900 p-8 text-white">
            <MessageCircle className="h-5 w-5 text-bronze-300" />
            <h3 className="display mt-4 text-[28px]">Talk to us, any hour</h3>
            <p className="mt-3 text-[14px] leading-relaxed text-white/75">Our assistant can check availability, book you a viewing and answer questions about deposits, pets and paperwork — instantly. A member of the team follows up on every conversation.</p>
            <PillButton onClick={openSiteChat} variant="white" className="mt-6">Start a conversation</PillButton>
          </div>
          <div className="surface p-8">
            <KeyRound className="h-5 w-5 text-bronze-500" />
            <h3 className="display mt-4 text-[28px] text-ink-900">Already a resident?</h3>
            <p className="mt-3 text-[14px] leading-relaxed text-ink-600">Report a repair, see your rent and documents, or message the estates team from your account.</p>
            <PillLink to="/login" variant="ghost" small className="mt-6">Resident sign in</PillLink>
          </div>
          <div className="surface p-8">
            <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-bronze-500">Follow</div>
            <ul className="mt-4 flex gap-3">
              {FOOTER_MENU.Follow.map((s) => (
                <li key={s.label}><a href={s.to} target="_blank" rel="noreferrer" aria-label={s.label} className="grid h-10 w-10 place-items-center rounded-full border border-ink-200 text-ink-600 transition hover:border-ink-900 hover:bg-ink-900 hover:text-white"><SocialIcon name={s.label} /></a></li>
              ))}
            </ul>
            <p className="mt-5 text-[13px] leading-relaxed text-ink-500">Lotus Living, Greater London &amp; Dubai South.<br />Registered with The Property Ombudsman and the NRLA.</p>
          </div>
        </aside>
      </section>

      <section className="border-t border-ink-100 bg-cream">
        <div className="site-container py-24 lg:py-32">
          <h2 className="display text-[40px] text-ink-900 sm:text-[52px] lg:text-[64px]">Discover <em>our Locations</em></h2>
          <SchemeGrid className="mt-12" />
        </div>
      </section>
    </SiteLayout>
  );
}

/** lucide-react no longer ships brand marks, so the three we need live here. */
function SocialIcon({ name }: { name: string }) {
  const paths: Record<string, string> = {
    LinkedIn: 'M20.45 20.45h-3.55v-5.57c0-1.33-.03-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.94v5.67H9.36V9h3.41v1.56h.05c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.46v6.28zM5.34 7.43a2.06 2.06 0 1 1 0-4.12 2.06 2.06 0 0 1 0 4.12zM7.12 20.45H3.56V9h3.56v11.45zM22.22 0H1.77C.79 0 0 .77 0 1.73v20.54C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.73V1.73C24 .77 23.2 0 22.22 0z',
    Instagram: 'M12 2.16c3.2 0 3.58.01 4.85.07 3.25.15 4.77 1.69 4.92 4.92.06 1.27.07 1.65.07 4.85s-.01 3.58-.07 4.85c-.15 3.23-1.66 4.77-4.92 4.92-1.27.06-1.64.07-4.85.07s-3.58-.01-4.85-.07c-3.26-.15-4.77-1.7-4.92-4.92C2.17 15.58 2.16 15.2 2.16 12s.01-3.58.07-4.85C2.38 3.92 3.9 2.38 7.15 2.23 8.42 2.17 8.8 2.16 12 2.16zM12 0C8.74 0 8.33.01 7.05.07 2.7.27.27 2.69.07 7.05.01 8.33 0 8.74 0 12s.01 3.67.07 4.95c.2 4.36 2.62 6.78 6.98 6.98C8.33 23.99 8.74 24 12 24s3.67-.01 4.95-.07c4.35-.2 6.78-2.62 6.98-6.98.06-1.28.07-1.69.07-4.95s-.01-3.67-.07-4.95C23.73 2.7 21.31.27 16.95.07 15.67.01 15.26 0 12 0zm0 5.84A6.16 6.16 0 1 0 12 18.16 6.16 6.16 0 0 0 12 5.84zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.4-11.85a1.44 1.44 0 1 0 0 2.88 1.44 1.44 0 0 0 0-2.88z',
    Facebook: 'M24 12.07C24 5.4 18.63 0 12 0S0 5.4 0 12.07C0 18.1 4.39 23.1 10.13 24v-8.44H7.08v-3.49h3.05V9.41c0-3.02 1.79-4.69 4.53-4.69 1.31 0 2.68.24 2.68.24v2.97h-1.51c-1.49 0-1.96.93-1.96 1.88v2.26h3.33l-.53 3.49h-2.8V24C19.61 23.1 24 18.1 24 12.07z',
  };
  return <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden="true"><path d={paths[name] ?? ''} /></svg>;
}
